import React, { useState, useEffect } from 'react';
import VisaIcon from './icons/VisaIcon';
import MastercardIcon from './icons/MastercardIcon';
import MirIcon from './icons/MirIcon';
import AmexIcon from './icons/AmexIcon';
import GenericIcon from './icons/GenericIcon';

interface CardInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

type CardBrand = 'visa' | 'mastercard' | 'mir' | 'amex' | 'unknown';

const CardInput: React.FC<CardInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = "xxxx yyyy xxxx yyyy",
  className = ""
}) => {
  const [cardBrand, setCardBrand] = useState<CardBrand>('unknown');

  // Определение типа карты по первым цифрам
  const detectCardBrand = (cardNumber: string): CardBrand => {
    const cleanNumber = cardNumber.replace(/\s/g, '');

    if (/^4/.test(cleanNumber)) {
      return 'visa';
    }
    if (/^220[0-4]/.test(cleanNumber)) {
      return 'mir';
    }
    // Mastercard: 51-55, 2221-2720
    if (/^5[1-5]/.test(cleanNumber) || /^2(2[2-9][1-9]|2[3-9][0-9]|[3-6][0-9]{2}|7[01][0-9]|720)/.test(cleanNumber)) {
      return 'mastercard';
    }
    if (/^3[47]/.test(cleanNumber)) {
      return 'amex';
    }
    return 'unknown';
  };

  // Форматирование номера карты
  const formatCardNumber = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    const brand = detectCardBrand(cleanValue);
    
    let formatted = '';
    if (brand === 'amex') {
      // American Express: XXXX XXXXXX XXXXX
      formatted = cleanValue.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
    } else {
      // Visa, Mastercard, MIR: XXXX XXXX XXXX XXXX
      formatted = cleanValue.replace(/(\d{4})(?=\d)/g, '$1 ');
    }
    
    return formatted.trim();
  };

  // Обработка изменения значения
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatCardNumber(inputValue);
    
    // Ограничиваем длину в зависимости от типа карты
    const cleanValue = formattedValue.replace(/\s/g, '');
    const brand = detectCardBrand(cleanValue);
    
    let maxLength = 19; // Visa, Mastercard, MIR
    if (brand === 'amex') {
      maxLength = 17; // 15 цифр + 2 пробела
    }
    
    if (cleanValue.length <= maxLength) {
      onChange(formattedValue);
    }
  };

  // Обновляем тип карты при изменении значения
  useEffect(() => {
    setCardBrand(detectCardBrand(value));
  }, [value]);

  // Используем настоящие SVG-иконки
  const getCardIcon = (brand: CardBrand) => {
    switch (brand) {
      case 'visa': return <VisaIcon />;
      case 'mastercard': return <MastercardIcon />;
      case 'mir': return <MirIcon />;
      case 'amex': return <AmexIcon />;
      default: return <GenericIcon />;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full px-4 py-3 pr-14 bg-[#181A20] border border-[#353945] rounded-lg placeholder-[#6C757D] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          disabled 
            ? 'text-[#6C757D] cursor-not-allowed opacity-50' 
            : 'text-white cursor-text'
        }`}
        maxLength={cardBrand === 'amex' ? 17 : 19}
      />
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
        {getCardIcon(cardBrand)}
      </div>
    </div>
  );
};

export default CardInput; 